import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ParticipantTile,
  TrackMutedIndicator,
  VideoTrack,
  useIsMuted,
  useIsSpeaking,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react';
import { isTrackReference } from '@livekit/components-core';
import { ParticipantEvent, Track } from 'livekit-client';
import {
  AudioLines,
  EyeOff,
  Hand,
  Mic,
  MicOff,
  MoreVertical,
  PhoneOff,
  Pin,
  PinOff,
  ScreenShare,
  UserMinus,
  Video,
  VideoOff,
} from 'lucide-react';
import LogoIconLight from '@/assets/images/logo-icone-light.svg';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ParticipantModerationAction } from './types';
import { participantAvatarUrl } from './participantAvatar';
import { participantClientKind } from './participantClient';
import { participantHasRaisedHand } from './participantHandRaise';
import { participantCanPublishSource } from './participantPermissions';

const participantColors = [
  '#2563EB',
  '#059669',
  '#D97706',
  '#DC2626',
  '#7C3AED',
  '#0891B2',
  '#BE185D',
];

const participantColor = (identity: string) => {
  const hash = Array.from(identity).reduce(
    (value, character) => (value * 31 + character.charCodeAt(0)) | 0,
    0,
  );
  return participantColors[Math.abs(hash) % participantColors.length];
};

const ParticipantCameraTile = ({
  trackRef,
  className,
  onModerateParticipant,
  moderationPending = false,
  isLayoutPinned = false,
  onToggleLayoutPin,
  onHideFromLayout,
}: {
  trackRef: TrackReferenceOrPlaceholder;
  className: string;
  onModerateParticipant?: (
    participantIdentity: string,
    action: ParticipantModerationAction,
    reason?: string,
  ) => void;
  moderationPending?: boolean;
  isLayoutPinned?: boolean;
  onToggleLayoutPin?: () => void;
  onHideFromLayout?: () => void;
}) => {
  const { t } = useTranslation();
  const [removeConfirmationOpen, setRemoveConfirmationOpen] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [, setPermissionVersion] = useState(0);
  const [, setMetadataVersion] = useState(0);
  const isMuted = useIsMuted(trackRef);
  const isSpeaking = useIsSpeaking(trackRef.participant);
  const participantName =
    trackRef.participant.name ||
    trackRef.participant.identity ||
    'Participante';
  const isLocalParticipant = trackRef.participant.isLocal;
  const displayName = isLocalParticipant ? 'Você' : participantName;
  const initial = participantName.trim().charAt(0).toUpperCase() || 'P';
  const avatarUrl = participantAvatarUrl(trackRef.participant);
  const shouldShowAvatar = Boolean(avatarUrl) && !avatarLoadFailed;
  const hasVideo = Boolean(trackRef.publication?.track) && !isMuted;
  const isScreenShare = trackRef.source === Track.Source.ScreenShare;
  const hasLayoutActions = Boolean(onToggleLayoutPin || onHideFromLayout);
  const canModerate =
    Boolean(onModerateParticipant) &&
    !trackRef.participant.isLocal &&
    !isScreenShare &&
    trackRef.participant.identity.startsWith('candidate-');
  const microphoneAllowed = participantCanPublishSource(
    trackRef.participant,
    Track.Source.Microphone,
  );
  const cameraAllowed = participantCanPublishSource(
    trackRef.participant,
    Track.Source.Camera,
  );
  const handRaised = participantHasRaisedHand(trackRef.participant.metadata);
  const clientKind = participantClientKind(trackRef.participant.metadata);
  const clientTitle =
    clientKind === 'mobile_app'
      ? 'Entrou pelo app'
      : clientKind === 'mobile_web'
        ? 'Entrou pelo navegador mobile'
        : '';
  const trimmedRemoveReason = removeReason.trim();

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [avatarUrl]);

  useEffect(() => {
    const handlePermissionsChanged = () => {
      setPermissionVersion((version) => version + 1);
    };
    trackRef.participant.on(
      ParticipantEvent.ParticipantPermissionsChanged,
      handlePermissionsChanged,
    );
    return () => {
      trackRef.participant.off(
        ParticipantEvent.ParticipantPermissionsChanged,
        handlePermissionsChanged,
      );
    };
  }, [trackRef.participant]);

  useEffect(() => {
    const handleMetadataChanged = () => {
      setMetadataVersion((version) => version + 1);
    };
    trackRef.participant.on(
      ParticipantEvent.ParticipantMetadataChanged,
      handleMetadataChanged,
    );
    return () => {
      trackRef.participant.off(
        ParticipantEvent.ParticipantMetadataChanged,
        handleMetadataChanged,
      );
    };
  }, [trackRef.participant]);

  return (
    <div
      className={[
        'relative h-full w-full overflow-hidden rounded-[inherit]',
        className,
      ].join(' ')}
    >
      {isLocalParticipant ? (
        <span className="pointer-events-none absolute inset-0 z-10 rounded-[inherit] shadow-[inset_0_0_0_2px_rgba(125,211,252,0.95)]" />
      ) : null}
      {hasVideo && isScreenShare && isTrackReference(trackRef) ? (
        <VideoTrack
          trackRef={trackRef}
          className="h-full w-full bg-black object-contain"
        />
      ) : hasVideo && isTrackReference(trackRef) ? (
        <VideoTrack trackRef={trackRef} className="h-full w-full" />
      ) : hasVideo ? (
        <ParticipantTile
          trackRef={trackRef}
          className="h-full w-full [&_.lk-participant-metadata]:hidden"
        />
      ) : (
        <div
          className="relative flex h-full w-full items-center justify-center p-6 text-white"
          style={{
            backgroundColor: participantColor(trackRef.participant.identity),
          }}
        >
          <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-white/20 text-4xl font-semibold shadow-[0_14px_36px_rgba(15,23,42,0.24)] ring-1 ring-white/30 sm:h-24 sm:w-24 sm:text-5xl">
            {shouldShowAvatar ? (
              <img
                src={avatarUrl}
                alt={displayName}
                className="h-full w-full object-cover"
                draggable={false}
                onError={() => setAvatarLoadFailed(true)}
              />
            ) : (
              initial
            )}
          </div>
        </div>
      )}
      {!isScreenShare ? (
        <div className="lk-participant-metadata">
          <div
            className={[
              'lk-participant-metadata-item',
              isLocalParticipant ? 'lk-local-participant-metadata-item' : '',
            ].join(' ')}
          >
            <TrackMutedIndicator
              trackRef={{
                participant: trackRef.participant,
                source: Track.Source.Microphone,
              }}
              show="muted"
            />
            {isLocalParticipant ? (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-sky-500 shadow-[0_0_0_3px_rgba(14,165,233,0.18)]"
                aria-hidden="true"
              />
            ) : null}
            <span
              className={[
                'min-w-0 truncate',
                isLocalParticipant ? 'text-sm font-semibold' : '',
              ].join(' ')}
            >
              {displayName}
            </span>
          </div>
        </div>
      ) : null}
      {!isScreenShare && handRaised ? (
        <div
          className={[
            'absolute right-3 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-amber-200/90 bg-amber-400 text-amber-950 shadow-[0_10px_24px_rgba(120,53,15,0.32)]',
            canModerate || hasLayoutActions ? 'top-14' : 'top-3',
          ].join(' ')}
          aria-label="Mão levantada"
          title="Mão levantada"
        >
          <Hand className="h-5 w-5 shrink-0" strokeWidth={2.35} />
        </div>
      ) : null}
      {isSpeaking ? (
        <>
          <span className="pointer-events-none absolute inset-0 z-10 rounded-[inherit] shadow-[inset_0_0_0_4px_#34d399]" />
          <span
            className="absolute left-3 top-3 z-20 flex h-7 w-7 animate-pulse items-center justify-center rounded-full border-2 border-white/90 bg-emerald-400 text-emerald-950 shadow-lg"
            aria-label="Falando"
            title="Falando"
          >
            <AudioLines size={15} strokeWidth={2.5} />
          </span>
        </>
      ) : null}
      {!isScreenShare && clientKind ? (
        <span
          className={[
            'absolute z-20 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white/85 bg-blue-600 shadow-[0_8px_18px_rgba(15,23,42,0.32)] sm:h-9 sm:w-9',
            isSpeaking ? 'left-12 top-3' : 'left-3 top-3',
          ].join(' ')}
          title={clientTitle}
          aria-label={clientTitle}
        >
          <img
            src={LogoIconLight}
            alt=""
            className="h-4 w-4 object-contain brightness-0 invert sm:h-5 sm:w-5"
            draggable={false}
          />
        </span>
      ) : null}
      {isScreenShare ? (
        <div className="lk-participant-metadata">
          <div className="lk-participant-metadata-item">
            <ScreenShare className="mr-1 h-4 w-4 shrink-0" />
            <span className="truncate">
              {t('teleattendance.screen_shared_by', { name: participantName })}
            </span>
          </div>
        </div>
      ) : null}
      {canModerate || hasLayoutActions ? (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="absolute right-3 top-3 z-30 h-9 w-9 rounded-full bg-black/65 text-white shadow-md hover:bg-black/80 hover:text-white"
                aria-label={`Ações para ${participantName}`}
                title={`Ações para ${participantName}`}
                disabled={moderationPending}
              >
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              {onToggleLayoutPin ? (
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    onToggleLayoutPin();
                  }}
                >
                  {isLayoutPinned ? (
                    <PinOff className="mr-2 h-4 w-4" />
                  ) : (
                    <Pin className="mr-2 h-4 w-4" />
                  )}
                  {isLayoutPinned ? 'Desafixar câmera' : 'Fixar câmera'}
                </DropdownMenuItem>
              ) : null}
              {onHideFromLayout ? (
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    onHideFromLayout();
                  }}
                >
                  <EyeOff className="mr-2 h-4 w-4" />
                  Ocultar câmera
                </DropdownMenuItem>
              ) : null}
              {hasLayoutActions && canModerate ? <DropdownMenuSeparator /> : null}
              {canModerate ? (
                <>
                  {microphoneAllowed ? (
                    <DropdownMenuItem
                      disabled={moderationPending}
                      onSelect={() =>
                        onModerateParticipant?.(
                          trackRef.participant.identity,
                          'mute_microphone',
                        )
                      }
                    >
                      <MicOff className="mr-2 h-4 w-4" />
                      Silenciar microfone
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      disabled={moderationPending}
                      onSelect={() =>
                        onModerateParticipant?.(
                          trackRef.participant.identity,
                          'allow_microphone',
                        )
                      }
                    >
                      <Mic className="mr-2 h-4 w-4" />
                      Permitir microfone
                    </DropdownMenuItem>
                  )}
                  {cameraAllowed ? (
                    <DropdownMenuItem
                      disabled={moderationPending}
                      onSelect={() =>
                        onModerateParticipant?.(
                          trackRef.participant.identity,
                          'disable_camera',
                        )
                      }
                    >
                      <VideoOff className="mr-2 h-4 w-4" />
                      Desligar câmera
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      disabled={moderationPending}
                      onSelect={() =>
                        onModerateParticipant?.(
                          trackRef.participant.identity,
                          'allow_camera',
                        )
                      }
                    >
                      <Video className="mr-2 h-4 w-4" />
                      Permitir câmera
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={moderationPending}
                    onSelect={() =>
                      onModerateParticipant?.(
                        trackRef.participant.identity,
                        'disconnect',
                      )
                    }
                  >
                    <PhoneOff className="mr-2 h-4 w-4" />
                    Somente desconectar
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    disabled={moderationPending}
                    onSelect={() => setRemoveConfirmationOpen(true)}
                  >
                    <UserMinus className="mr-2 h-4 w-4" />
                    Expulsar permanentemente
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog
            open={removeConfirmationOpen}
            onOpenChange={(open) => {
              setRemoveConfirmationOpen(open);
              if (!open) setRemoveReason('');
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Expulsar permanentemente {participantName}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  O participante será desconectado, voltará para a tela de
                  entrada e não poderá entrar novamente nesta entrevista.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="grid gap-2">
                <label
                  htmlFor={`remove-reason-${trackRef.participant.sid}`}
                  className="text-sm font-medium text-foreground"
                >
                  Motivo da expulsão
                </label>
                <textarea
                  id={`remove-reason-${trackRef.participant.sid}`}
                  value={removeReason}
                  onChange={(event) => setRemoveReason(event.target.value)}
                  maxLength={500}
                  rows={4}
                  className="min-h-[96px] resize-none rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Descreva o motivo para registrar e informar o candidato."
                />
                <p className="text-xs text-muted-foreground">
                  {removeReason.length}/500 caracteres
                </p>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={!trimmedRemoveReason || moderationPending}
                  onClick={() =>
                    onModerateParticipant?.(
                      trackRef.participant.identity,
                      'remove',
                      trimmedRemoveReason,
                    )
                  }
                >
                  Expulsar permanentemente
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : null}
    </div>
  );
};

export default ParticipantCameraTile;
